import React from 'react';
import { BRAND } from '../theme/tokens';

/**
 * Canonical AI sparkle mark.
 *
 * Source of truth: https://fi-ai-styling-guide-3413f27b698e.herokuapp.com/pattern.html
 *
 * Two variants, locked by the AI Styling Pattern:
 *
 *   variant="two-star"  (default)   ≥ 16 px  — hero badges, AI pill CTA buttons,
 *                                            provenance tags, circular badges
 *   variant="single"                ≤ 14 px  — inline chips, tooltip icons,
 *                                            compact menu items, small-area contexts
 *
 * Rules (NEVER break):
 *   - Colour is always #39006B (BRAND) — or white on brand-purple surfaces.
 *   - Never animated (exception: spinning inside an in-progress AI pill).
 *   - Never used for non-AI features ("Premium", "New", decorative).
 *   - Never placed inside destructive-action buttons (delete, cancel).
 *
 * Usage:
 *     <Sparkle size={16} />                            // two-star, brand purple, 16 px
 *     <Sparkle size={12} variant="single" />          // single sparkle for inline
 *     <Sparkle size={24} color="#fff" />              // on a brand-purple surface
 *     <Sparkle size={18} title="AI parsed" />         // a11y label
 */

export type SparkleVariant = 'two-star' | 'single';

export interface SparkleProps {
  /** Pixel size (width + height). Defaults to 16. */
  size?: number;
  /** Mark variant. Defaults to 'two-star' for ≥ 16 px, 'single' for < 16 px. */
  variant?: SparkleVariant;
  /** Fill colour. Defaults to BRAND (#39006B). Use '#fff' on brand-purple surfaces. */
  color?: string;
  /** Accessible label. If set, the SVG is announced to screen readers. */
  title?: string;
  /** Inline-block styling for alignment with adjacent text. */
  inline?: boolean;
  /** Extra style overrides. */
  style?: React.CSSProperties;
}

export const Sparkle: React.FC<SparkleProps> = ({
  size = 16,
  variant,
  color = BRAND,
  title,
  inline = true,
  style,
}) => {
  // Auto-pick variant by size if not specified
  const resolvedVariant: SparkleVariant = variant ?? (size >= 16 ? 'two-star' : 'single');

  const common = {
    width: size,
    height: size,
    style: {
      display: inline ? 'inline-block' : 'block',
      flexShrink: 0,
      verticalAlign: inline ? 'middle' : undefined,
      ...style,
    },
    'aria-hidden': title ? undefined : true,
    role: title ? 'img' : undefined,
  };

  if (resolvedVariant === 'single') {
    return (
      <svg
        viewBox="0 0 24 24"
        fill={color}
        xmlns="http://www.w3.org/2000/svg"
        {...common}
      >
        {title ? <title>{title}</title> : null}
        <path d="M12 0 L14 9 L24 12 L14 15 L12 24 L10 15 L0 12 L10 9 Z" />
      </svg>
    );
  }

  // two-star: big star + small accent star (canonical)
  return (
    <svg
      viewBox="-50 -50 100 100"
      fill={color}
      xmlns="http://www.w3.org/2000/svg"
      {...common}
    >
      {title ? <title>{title}</title> : null}
      <path d="M 0 -36 L 8 -8 L 36 0 L 8 8 L 0 36 L -8 8 L -36 0 L -8 -8 Z" />
      <path d="M -32 -32 L -29 -22 L -19 -20 L -29 -18 L -32 -8 L -35 -18 L -45 -20 L -35 -22 Z" />
    </svg>
  );
};

export default Sparkle;
